/*
 *   Copyright (c) 2021 vlinder Labs Private Limited
 *   All rights reserved.
 *   This software is proprietary of Vlinder Labs Pvt. Ltd and can not be distributed / used for any purpose outside of Vlinder Labs Pvt. Ltd without explicit written consent from the copyright owner.
 */

import {
  IAgentPlugin,
  IPluginMethodMap,
} from "@vlinder-web/plugin-agent-service-react";
import config from "../config/featuresSection.config.json";
import { VApplication } from "../../../application";
import { BreadcrumbService } from "../../breadcrumbService";

import { GraphqlServerModule } from "@vlinder-web/graphql-server-module-react";
import { GET_POLICY_HOLDER_DETAIL } from "../graphql";
import { toHeaderCase, toLowerCase, toSentenceCase } from "js-convert-case";
import { useUserId } from "../provider";

import { AppNavigatorService } from "../../appNavigatorService";

const getWhoamiSrv = () => {
  const app = VApplication.getInstance();
  return app.getSync("services.AppNavigatorService") as AppNavigatorService;
};

export interface IFeatureSectionMethods extends IPluginMethodMap {
  getFeatureSectionProps(args: any): Promise<any>;
}

const lookup = require("country-code-lookup");

const getGraphQLModule = () => {
  const app = VApplication.getInstance();
  return app.getSync("services.GraphqlServerModule") as GraphqlServerModule;
};

export class FeatureSectionService implements IAgentPlugin {
  readonly methods: IFeatureSectionMethods;
  constructor() {
    this.methods = {
      getFeatureSectionProps: this.getProps.bind(this),
    };
  }

  async getProps(args: any) {
    const graphqlModule = getGraphQLModule();
    const client = graphqlModule.getClient("graphql-gateway");
    const { userId } = useUserId();
    try {
      if (client) {
        const res = await client.query({
          query: GET_POLICY_HOLDER_DETAIL,
          variables: {
            input: {
              vuid: userId,
            },
          },
          fetchPolicy: "network-only",
        });
        if (res?.data?.getUser) {
          let _res = res?.data?.getUser;
          let kycStatus = this.getKycStatus(_res?.kyc);
          console.log("test" + JSON.stringify(_res));
          return {
            title: `${toHeaderCase(_res?.firstName ?? "-")} ${toHeaderCase(
              _res?.lastName ?? "-"
            )}`,
            subtitle: _res?.occupation,
            kycSubmitted: _res?.kycSubmitted,
            profileStatus: this.getStatus(_res?.kyc),
            profileStatusDescription: this.getStatusDesc(_res?.kyc),
            heroImage: {
              image:
                _res?.image ??
                `https://ui-avatars.com/api/?name=${_res.firstName}+${_res?.lastName}&background=0D8ABC&color=fff`,
            },
            listItems: this.getListItems(_res, kycStatus),
            carouselItems: null,
            kycAccordionItems: this.getkycCarouselItems(_res?.kyc),
            buttons: [],
            kycAccordionButtons: await this.getKycAccordionButtons(_res?.kyc),
          };
        }
      }
    } catch (err: any) {
      console.log(`Sign In user error`, err?.response?.data);
      throw Error(err?.response?.data?.error?.display_message);
    }
  }

  private getKycStatus(items: any[]) {
    if (!Array.isArray(items) || items.length === 0) return "NOT_SUBMITTED";

    const firstKyc = items[0];
    const statuses = firstKyc?.docs?.map((d: any) => d?.status) || [];

    if (statuses.length === 0) return "NOT_SUBMITTED";

    if (statuses.every((s: string) => s === "VERIFIED")) {
      return "VERIFIED";
    } else if (statuses.some((s: string) => s === "REJECTED")) {
      return "REJECTED";
    } else if (statuses.some((s: string) => s === "SUBMITTED")) {
      return "SUBMITTED";
    }

    return "PENDING";
  }

  private getCarouselItems(items: any) {
    let _temp: any[] = [];

    if (Array.isArray(items) && items[0]?.docs) {
      _temp = items[0].docs.map((ele: any) => ({
        ...ele,
        type: this.getDocType(ele?.fileType),
        src: ele?.doc,
        title: ele?.name,
        poster: ele?.poster,
      }));
    }
    console.log("Temp" + JSON.stringify(_temp));

    return _temp;
  }

  private getkycCarouselItems(items: any) {
    let _temp: any = [];
    _temp =
      Array.isArray(items) &&
      items?.map((ele: any) => {
        return {
          ...ele,
          type: this.getDocType(ele?.fileType),
          src: ele?.doc,
          title: ele?.name,
          poster: ele?.poster,
          kycStatus: this.getKycStatus(ele?.status)
        };
      });
    return _temp;
  }

  private getDocType(res: any) {
    switch (res) {
      case "application/pdf":
        return "pdf";
      case "image/png":
        return "img";
      case "image/jpg":
        return "img";
      case "image/jpeg":
        return "img";
      default:
        return null;
    }
  }

  private async getButtons(res: any) {
    let _buttons: any = [];

    const allDocs = res?.kyc?.flatMap((k: any) => k.docs || []);

    const addressProof = allDocs?.find((i: any) => i?.type === "ADDRESS_PROOF");

    console.log("All docs =", JSON.stringify(allDocs, null, 2));

    let whoAmI = await getWhoamiSrv()?.whoAmI();
    if (whoAmI?.whoami === "RISK_CAPITAL") {
      return [];
    } else if (addressProof?.status === "SUBMITTED") {
      _buttons.push(config?.approveButton);
    } else if (addressProof?.status === "REJECTED") {
      _buttons.push(config?.approveButton);
    }

    return _buttons;
  }

  private async getKycAccordionButtons(kycArr: any[]) {
    if (!Array.isArray(kycArr)) return {};

    const whoAmI = await getWhoamiSrv()?.whoAmI();
    if (whoAmI?.whoami === "RISK_CAPITAL") return {};

    const result: Record<string, any[]> = {};

    kycArr.forEach((kyc) => {
      const docs = kyc.docs || [];
      const addressProof = docs.find((d: any) => d.type === "ADDRESS_PROOF");

      const accordionKey = docs[0]?._id || Math.random().toString();

      if (addressProof?.status === "SUBMITTED") {
        result[accordionKey] = [config?.approveButton];
      } else {
        result[accordionKey] = [];
      }
    });

    return result;
  }

  private getStatus(items: any) {
    let tempStatus = items && items[0];
    if (tempStatus?.status) {
      return toLowerCase(tempStatus?.status ?? "");
    } else return null;
  }

  private getStatusDesc(items: any) {
    let tempStatus = items && items[0];
    if (tempStatus?.comment) {
      return toSentenceCase(tempStatus?.comment ?? "");
    } else return null;
  }

  private getListItems(res: any, res1: any) {
    let _temp = [];
    _temp = config?.listItems?.map((item) => {
      if (item?.title === "Full Name") {
        return {
          ...item,
          subTitle: `${toHeaderCase(res?.firstName ?? "")} ${toHeaderCase(
            res?.lastName ?? ""
          )}`,
        };
      }
      if (item?.title === "Street Address") {
        return {
          ...item,
          subTitle: res?.address?.addressLine1
            ? `${res?.address?.addressLine1}`
            : "-",
        };
      }
      if (item?.title === "City") {
        return {
          ...item,
          subTitle: res?.address?.city ?? "-",
        };
      }
      if (item?.title === "Country") {
        return {
          ...item,
          subTitle:
            res?.address?.country && res?.address?.country.length === 2
              ? lookup.byIso(`${res?.address?.country}`)?.country
              : "-",
          flag: res?.address?.country,
        };
      }
      if (item?.title === "Email") {
        return {
          ...item,
          subTitle: res?.email ?? "-",
          verified: res?.emailVerified,
        };
      }
      if (item?.title === "Telephone") {
        return {
          ...item,
          subTitle: res?.primaryContactNo ?? "-",
          verified: res?.phoneVerified,
        };
      }
      if (item?.title === "Occupation") {
        return {
          ...item,
          subTitle: res?.occupation ?? "-",
        };
      }
      if (item?.title === "Status") {
        return null;
      }
    });
    return _temp.filter(Boolean);
  }
}
